import { Box, Divider, Spacer } from '@eatclubapp/ec-component-library';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getPauseFeesAction,
  reactivateRestaurantPauseAction,
} from '../../actions/cancellationAction';
import { fetchNetBalanceByRestIdAction } from '../../actions/settlementsAction';
import { trackDetailedEvent } from '../../utils/analytics';
import { isEmpty } from '../../utils/baseHelpers';
import { formatCurrency, formatRestaurantAddress } from '../../utils/helpers';
import { useAppConfig } from '../../utils/hooks/useAppConfig';
import useStyles from './UnpauseModalStyles';
import RightArrowSVG from '../../assets/icons/arrow_large.svg';
import { FeatureModal } from '../Modal/FeatureModal';
import CheckInCircle from '../../assets/icons/check_in_circle_no_outline.svg';
import PhoneSVG from '../../assets/images/phone.svg';

const UnpauseModal = ({ isOpen, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const appConfig = useAppConfig();

  const [triedSaving, setTriedSaving] = useState(false); // To prevent the useEffect auto closing when adding multiple cards
  const [saving, setSaving] = useState(false); // Children can explicitly say they are saving
  const activeRestaurant = useSelector((state) => state.restaurantActive.restaurant);
  const pauseFees = useSelector((state) => state.pauseFees);
  const cancellation = useSelector((state) => state.cancellation);
  const netBalance = useSelector((state) => state.netBalance);
  const dispatch = useDispatch();

  const hasOutstandingDebt = netBalance.success && (netBalance.data?.netBalance ?? 0) < 0;
  const isSaving = cancellation.pending || saving;
  const [errorMessage, setErrorMessage] = useState(null);

  const [success, setSuccess] = useState(false);

  // Fetch data on start
  useEffect(() => {
    if (isOpen) {
      dispatch(getPauseFeesAction());
      dispatch(fetchNetBalanceByRestIdAction());
    }
  }, [isOpen, dispatch]);

  // Reset state when opened
  useEffect(() => {
    setErrorMessage(null);
    setSaving(false);
    setTriedSaving(false);
    setSuccess(false);
  }, [isOpen]);

  // On save completion
  useEffect(() => {
    if (triedSaving && cancellation.success && !cancellation.error) {
      setTriedSaving(false); // For next time it's opened
      onClose();
    }
  }, [cancellation.success, cancellation.error, onClose]);

  const handleConfirm = async (event) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();
    trackDetailedEvent('Pause reactivation confirmed');

    setTriedSaving(true);
    setSaving(true);
    setErrorMessage(null);

    dispatch(reactivateRestaurantPauseAction());
  };

  // TODO fetch from API
  const restaurantData = {
    services: appConfig.takeawayBlocked ? ['dineIn'] : ['dineIn', 'takeaway'],
    commission: pauseFees?.data?.dineInOnlinePercent,
    membership: pauseFees?.data?.membershipFee,
    rebate: pauseFees?.data?.rebatePercentage ?? 0,
  };

  const getServiceText = () => {
    if (isEmpty(restaurantData?.services)) {
      return '-';
    }

    const serviceMap = {
      dineIn: 'dine-in',
      takeaway: 'takeaway',
    };

    const serviceText = restaurantData.services.map((service) => serviceMap?.[service]).join(', ');
    return `More customers for ${serviceText}`;
  };

  if (!activeRestaurant) {
    return null;
  }

  // Success state
  if (success) {
    return (
      <FeatureModal
        isOpen={isOpen}
        onClose={onClose}
        disabled={false}
        inputStyle={{ maxWidth: '600px' }}
        isLoading={isSaving}
        onConfirm={() => {
          onClose();
          history.push('/offers');
        }}
        confirmLabel='View offers'
        hideCancelButton
      >
        <Box className={classes.unpauseSuccessContent}>
          <PhoneSVG style={{ paddingRight: '23px' }} />
          <Box className={classes.unpauseSuccessContentInner}>
            <Box className={classes.title}>Welcome back</Box>
            <Box style={{ maxWidth: '350px', textAlign: 'center' }}>
              We’re excited to have you back on the platform.
              <br /> A few things to know:
            </Box>
            <Divider color='#E3E3E3' />
            <Box className={classes.pointsContainer}>
              <Spacer gap={12}>
                <CheckInCircle color={theme.colors.checkBackground} />
                <Box className={classes.description}>
                  Your profile is live and ready for new customers.
                </Box>
              </Spacer>

              <Spacer gap={12}>
                <CheckInCircle color={theme.colors.checkBackground} />
                <Box className={classes.description}>
                  Any active offers are visible in the app right now.
                </Box>
              </Spacer>

              <Spacer gap={12}>
                <CheckInCircle color={theme.colors.checkBackground} />
                <Box className={classes.description}>
                  You can update or add offers any time in your Partner Portal
                </Box>
              </Spacer>
            </Box>
          </Box>
        </Box>
      </FeatureModal>
    );
  }

  return (
    <FeatureModal
      isOpen={isOpen}
      onClose={() => {
        trackDetailedEvent('Pause reactivation cancelled');
        onClose();
      }}
      heading='Ready to welcome diners again?'
      confirmLabel={
        <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Box>Reactivate account</Box>
          <RightArrowSVG style={{ height: '14px', width: '14px', marginBottom: '-2px' }} />
        </Box>
      }
      onConfirm={handleConfirm}
      disabled={isSaving || hasOutstandingDebt}
      inputStyle={{ maxWidth: '600px' }}
      isLoading={isSaving}
    >
      <Box className={classes.unpauseModalContent}>
        <Box>
          Once reactivated, your offers will be visible immediately to customers so you can start
          filling tables right away.
        </Box>

        <Box className={classes.detailsContainer}>
          <Box className={classes.venueName}>{activeRestaurant?.name}</Box>
          <Box className={classes.text} style={{ paddingBottom: '16px', paddingTop: '8px' }}>
            {formatRestaurantAddress(activeRestaurant)}
          </Box>
          <Divider />
          <Box className={classes.table}>
            <Box className={classes.tableLabel}>Services</Box>
            <Box>{getServiceText()}</Box>

            <Box className={classes.tableLabel}>Commission</Box>
            <Box>{restaurantData?.commission}% per transaction</Box>

            <Box className={classes.tableLabel}>Membership</Box>
            <Box>
              {formatCurrency(restaurantData?.membership, false, '-', false, false, appConfig)}{' '}
              monthly
            </Box>

            {restaurantData.rebate > 0 && (
              <>
                <Box className={classes.tableLabel}>EatClub credit</Box>
                <Box>{restaurantData.rebate}% on every transaction</Box>
              </>
            )}
          </Box>
        </Box>

        {hasOutstandingDebt && (
          <Box style={{ marginBottom: '8px' }}>
            You have an outstanding balance. Please clear it before reactivating.
          </Box>
        )}
      </Box>
    </FeatureModal>
  );
};

export default UnpauseModal;
